import React, { useState, Fragment, useEffect, useMemo } from "react";
import { makeStyles, TextField } from "@material-ui/core";
import { SearchDialog } from "./SearchDialog";
import { useIsMounted } from "@ofqual/portal-core/core/hooks";
import { SearchFieldProps } from "../types";

const useStyles = makeStyles(() => ({
  readOnlyInput: {},
}));

export const SearchFieldSingle = React.forwardRef<unknown, SearchFieldProps>(
  (props) => {
    const { onChange, value, lookupTracker, ...coreProps } = props;
    const {
      label,
      service,
      addservice,
      disabled,
      datatest,
      name,
      readOnly,
      InputProps,
      variant,
    } = coreProps;
    const classes = useStyles();

    const [lookedUpValue, setLookedUpValue] = useState<{
      id: string | number;
      label: string;
    } | null>(null);

    const [dialogOpenProps, setDialogOpenProps] = useState({
      isDialogOpen: false,
      initialSearchText: "",
    });

    const isMounted = useIsMounted();

    useEffect(() => {
      (async () => {
        if (!value || (lookedUpValue && lookedUpValue.id === value)) {
          return;
        }
        const result = await service.get(String(value));
        if (result?.label && name && lookupTracker) {
          lookupTracker.register({
            name,
            id: value,
            label: result.label,
          });
        }
        isMounted.current &&
          setLookedUpValue(
            (result as { id: string | number; label: string }) || null
          );
      })();
    }, [isMounted, value, service, lookedUpValue, lookupTracker, name]);

    // reset state values if value is empty and currently have values left in state
    if (!value && lookedUpValue) {
      setLookedUpValue(null);
    }

    const handleSelectOrClose = (val?: { id: string; label: string }) => {
      setDialogOpenProps({ isDialogOpen: false, initialSearchText: "" });
      if (val) {
        handleAdd(val);
      }
    };

    const handleAdd = (val: { id: string; label: string }) => {
      setLookedUpValue(val);
      if (name && lookupTracker) {
        lookupTracker.register({
          name,
          id: val.id,
          label: val.label,
        });
      }
      onChange &&
        // we mimic the natural onChange shape of the underlying material-ui control,
        //  so build a shape that includes target:... etc. Also `type` is required so that
        //  `react-hook-form` processes the `onChange` argument like a material-ui argument,
        //  rather than in a primitive "the whole value I am getting back is the actual value" style
        //  see https://github.com/react-hook-form/react-hook-form/blob/3b880620fe2f2833501f17f15ea006d2fc0869fd/src/logic/getInputValue.ts#L8
        onChange(({
          type: true,
          name,
          target: {
            value: val.id,
          },
        } as unknown) as React.ChangeEvent<HTMLInputElement>);
    };

    const inputClassName = readOnly ? classes.readOnlyInput : undefined;
    const inputProps =
      readOnly === undefined
        ? InputProps
        : { ...InputProps, readOnly, className: inputClassName };

    const selectedItems = useMemo(
      () => (lookedUpValue ? [lookedUpValue] : undefined),
      [lookedUpValue]
    );

    const textLabel =
      readOnly === undefined || readOnly === false
        ? props.label
        : `${props.label} (Read Only)`;
    return (
      <Fragment>
        <TextField
          {...coreProps}
          data-test={datatest}
          fullWidth
          variant={variant || "outlined"}
          label={textLabel}
          disabled={disabled}
          onClick={() =>
            !disabled &&
            !readOnly &&
            setDialogOpenProps({ isDialogOpen: true, initialSearchText: "" })
          }
          onKeyPress={(e) =>
            !disabled &&
            !readOnly &&
            setDialogOpenProps({
              isDialogOpen: true,
              // if an alphanumeric character has been typed in the parent field, kick off the search with it
              initialSearchText: e.key.match(/^[A-Za-z0-9]{1}$/) ? e.key : "",
            })
          }
          value={lookedUpValue?.label || ""} // pass "" instead of null to stop Material UI console errors
          InputProps={inputProps}
        ></TextField>

        <SearchDialog
          label={label}
          service={service}
          dialogOpenProps={dialogOpenProps}
          onSelectOrClose={handleSelectOrClose}
          selectedItems={selectedItems}
          datatest={datatest}
          addservice={addservice}
        />
      </Fragment>
    );
  }
);
